import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNoIndex } from '@/hooks/useSEO';
import { GraduationCap } from 'lucide-react';

export default function Login() {
    useNoIndex('Sign in — Linguistic Quest');
    const { login } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const isTeacherHint = params.get('role') === 'teacher';
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setErr(null); setBusy(true);
        try {
            const u = await login(email, password);
            const next = params.get('next');
            if (u.must_change_password) {
                navigate('/onboarding/change-password', { replace: true });
            } else if (next && next.startsWith('/')) {
                // Trusted same-origin path only — never accept absolute URLs.
                navigate(next, { replace: true });
            } else if (u.role === 'admin') {
                navigate('/admin');
            } else if (u.role === 'teacher') {
                navigate('/teacher');
            } else {
                navigate('/dashboard');
            }
        } catch (e) { setErr(e?.response?.data?.detail || 'Login failed'); }
        finally { setBusy(false); }
    };
    return (
        <main className="max-w-md mx-auto px-6 py-16" data-testid="page-login">
            <h1 className="font-display text-4xl font-semibold text-lq-ink-black">{isTeacherHint ? 'Teacher sign in' : 'Welcome back.'}</h1>
            <p className="mt-2 text-sm text-lq-ink-500">{isTeacherHint ? 'Use the email your school admin created your account with. First time? Enter the one-time password from your welcome email — we’ll then ask you to choose a new one.' : 'Sign in to continue where your family left off.'}</p>
            {isTeacherHint && (
                <div className="mt-4 flex items-start gap-2 text-xs text-lq-purple-700 bg-lq-purple-50 border border-lq-purple-100 rounded-xl p-3" data-testid="login-teacher-hint">
                    <GraduationCap size={14} className="mt-0.5 flex-shrink-0"/>
                    <span>OTP looks like <code className="font-mono">LQ-XXXX-XXXX</code>. Lost it? Ask your administrator to resend.</span>
                </div>
            )}
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">Email</span>
                    <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="login-email" />
                </label>
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">{isTeacherHint ? 'Password or one-time password' : 'Password'}</span>
                    <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="login-password" />
                </label>
                {err && <div className="text-sm text-lq-error" role="alert">{err}</div>}
                <button disabled={busy} className="w-full bg-lq-purple-500 text-white font-semibold py-3 rounded-full hover:bg-lq-purple-700 disabled:opacity-60" data-testid="login-submit">
                    {busy ? 'Signing in…' : 'Sign in'}
                </button>
                <p className="text-sm text-lq-ink-500 text-center">
                    <Link to="/forgot-password" className="text-lq-purple-700 hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
                </p>
                <p className="text-sm text-lq-ink-500 text-center">
                    {isTeacherHint ? <>Are you a student? <Link to="/schools/join" className="text-lq-sky-600 underline">Use your join code →</Link></> : <>New here? <Link to="/register" className="text-lq-purple-700 underline">Create an account</Link></>}
                </p>
            </form>
        </main>
    );
}
